import { useMemo, useState } from 'react';
import type { CardBrand, VaultItem } from '@heybesafe/shared';
import { detectCardBrand, luhnValid, formatCardNumber } from '@heybesafe/shared';
import { HbsInput } from './hbs/Input';
import { HbsButton } from './hbs/Button';
import { EyeToggle } from './hbs/EyeToggle';
import { cardBrandLabel } from '../lib/cardBrandLabel';

/** Keep only digits — the stored `cardNumber` is digits-only; grouping is a
 *  display concern (formatCardNumber). */
function digitsOnly(s: string): string {
  return s.replace(/\D+/g, '');
}

/** The already-cleaned card payload the form hands back: `cardNumber` is
 *  digits-only and `cardBrand` is derived. Optional fields are omitted when
 *  blank so we never persist empty strings. */
export interface CardFormValues {
  name: string;
  cardholderName?: string;
  cardNumber?: string;
  cardBrand?: CardBrand;
  expMonth?: string;
  expYear?: string;
  cardSecurityCode?: string;
  cardBillingZip?: string;
}

export interface CardFormProps {
  heading: string;
  initial?: Partial<VaultItem>;
  isEdit: boolean;
  loading: boolean;
  onSubmit: (values: CardFormValues) => void;
  onCancel: () => void;
}

const MONTHS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));

export default function CardForm({
  heading,
  initial,
  isEdit,
  loading,
  onSubmit,
  onCancel,
}: CardFormProps): JSX.Element {
  const [name, setName] = useState(initial?.name ?? '');
  const [cardholderName, setCardholderName] = useState(initial?.cardholderName ?? '');
  // Stored digits-only; displayed grouped. Seeded from the stored digits.
  const [cardNumber, setCardNumber] = useState(digitsOnly(initial?.cardNumber ?? ''));
  const [expMonth, setExpMonth] = useState(initial?.expMonth ?? '');
  const [expYear, setExpYear] = useState(initial?.expYear ?? '');
  const [cardSecurityCode, setCardSecurityCode] = useState(initial?.cardSecurityCode ?? '');
  const [cardBillingZip, setCardBillingZip] = useState(initial?.cardBillingZip ?? '');
  const [cvvRevealed, setCvvRevealed] = useState(false);

  // Live, derived-as-you-type — never a hard gate. Brand feeds grouping + the
  // helper label; the Luhn warning is advisory only.
  const brand = useMemo<CardBrand>(() => detectCardBrand(cardNumber), [cardNumber]);
  const luhnWarning = cardNumber.length > 0 && !luhnValid(cardNumber);

  // Year picker: current year .. +20, always including a stored past year so an
  // old card still shows its real expiry when edited.
  const years = useMemo(() => {
    const current = new Date().getFullYear();
    const list: string[] = [];
    for (let y = current; y <= current + 20; y++) list.push(String(y));
    if (expYear && !list.includes(expYear)) list.unshift(expYear);
    return list;
  }, [expYear]);

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (!name.trim()) return;
    const digits = digitsOnly(cardNumber);
    onSubmit({
      name: name.trim(),
      cardholderName: cardholderName.trim() || undefined,
      cardNumber: digits || undefined,
      // Derive + store the brand on save so every client shows brand + last-4
      // without its own IIN table.
      cardBrand: digits ? detectCardBrand(digits) : undefined,
      expMonth: expMonth || undefined,
      expYear: expYear || undefined,
      // CVV is optional and never required to save.
      cardSecurityCode: cardSecurityCode.trim() || undefined,
      cardBillingZip: cardBillingZip.trim() || undefined,
    });
  }

  return (
    <div className="h-full flex flex-col max-w-3xl">
      <form id="card-editor-form" onSubmit={handleSubmit} className="flex-1 min-h-0 overflow-y-auto p-7 flex flex-col gap-4">
        <h2 className="font-display font-semibold text-[24px] tracking-hbs-tight text-hbs-text mb-2">
          {heading}
        </h2>
        <HbsInput label="Name" value={name} onChange={setName} placeholder="e.g. Personal Visa" required autoFocus name="name" recommended={!isEdit} />
        <HbsInput label="Cardholder name" value={cardholderName} onChange={setCardholderName} placeholder="Name as printed on the card" name="cardholderName" />
        <HbsInput
          label="Card number"
          value={formatCardNumber(cardNumber, brand)}
          onChange={(v) => setCardNumber(digitsOnly(v))}
          placeholder="1234 5678 9012 3456"
          name="cardNumber"
          mono
          helper={
            luhnWarning
              ? undefined
              : cardNumber
                ? cardBrandLabel(brand)
                : undefined
          }
          error={luhnWarning ? "This doesn't look like a valid card number" : undefined}
        />

        <div className="flex gap-4">
          <div className="flex-1">
            <label
              htmlFor="card-exp-month-input"
              className="block text-[11.5px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute mb-1"
            >
              Expiry month
            </label>
            <select
              id="card-exp-month-input"
              aria-label="Expiry month"
              value={expMonth}
              onChange={(e) => setExpMonth(e.target.value)}
              className="w-full h-12 px-3 bg-hbs-surface border-[1.5px] border-hbs-border rounded-hbs-sm text-[15px] text-hbs-text focus:outline-none focus-within:border-hbs-primary focus-within:ring-4 focus-within:ring-hbs-primary/20"
            >
              <option value="">MM</option>
              {MONTHS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label
              htmlFor="card-exp-year-input"
              className="block text-[11.5px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute mb-1"
            >
              Expiry year
            </label>
            <select
              id="card-exp-year-input"
              aria-label="Expiry year"
              value={expYear}
              onChange={(e) => setExpYear(e.target.value)}
              className="w-full h-12 px-3 bg-hbs-surface border-[1.5px] border-hbs-border rounded-hbs-sm text-[15px] text-hbs-text focus:outline-none focus-within:border-hbs-primary focus-within:ring-4 focus-within:ring-hbs-primary/20"
            >
              <option value="">YYYY</option>
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>

        <HbsInput
          label="Security code (CVV)"
          type={cvvRevealed ? 'text' : 'password'}
          value={cardSecurityCode}
          onChange={setCardSecurityCode}
          placeholder="Optional"
          name="cardSecurityCode"
          mono={cvvRevealed}
          trailingActions={<EyeToggle revealed={cvvRevealed} onChange={setCvvRevealed} label="security code" />}
        />
        <HbsInput label="Billing ZIP" value={cardBillingZip} onChange={setCardBillingZip} placeholder="Optional" name="cardBillingZip" />
      </form>
      <div className="shrink-0 border-t border-hbs-border bg-hbs-bg px-7 py-4 flex gap-2">
        <HbsButton id="card-save-button" type="submit" form="card-editor-form" loading={loading} fullWidth>
          Save
        </HbsButton>
        <HbsButton
          id="card-cancel-button"
          type="button"
          variant="ghost"
          fullWidth
          onClick={onCancel}
        >
          Cancel
        </HbsButton>
      </div>
    </div>
  );
}
